/**
 * Markdown editor component using react-md-editor.
 * Supports wiki-style hex linking with [[hex-name]] syntax.
 * Supports drag-and-drop image insertion from MediaLibrary.
 */

import { useState, useRef, useCallback } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { MEDIA_DRAG_TYPE } from '@/components/Media/MediaLibrary';
import type { UploadedMedia } from '@/components/Media/MediaUpload';

interface Props {
    /** Initial markdown content */
    value?: string | null;
    /** Callback when content changes */
    onChange?: (value: string) => void;
    /** Placeholder text when editor is empty */
    placeholder?: string;
    /** Minimum height of the editor */
    height?: number;
    /** Callback when an image is dropped - receives media and drop position */
    onImageDrop?: (media: UploadedMedia, position: { x: number; y: number }) => void;
}

/**
 * Markdown editor wrapper with dark theme styling.
 * Supports standard markdown plus [[hex-name]] wiki-style linking.
 * Supports drag-and-drop image insertion with visual feedback.
 */
export function Editor({ value, onChange, placeholder, height = 300, onImageDrop }: Props) {
    const [isDragOver, setIsDragOver] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    /**
     * Check if the drag event contains our media type.
     */
    const hasMediaData = useCallback((e: React.DragEvent): boolean => {
        return e.dataTransfer.types.includes(MEDIA_DRAG_TYPE);
    }, []);

    /**
     * Handle drag enter - show drop zone indicator.
     */
    const handleDragEnter = useCallback((e: React.DragEvent) => {
        if (!hasMediaData(e)) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, [hasMediaData]);

    /**
     * Handle drag over - allow drop and maintain indicator.
     */
    const handleDragOver = useCallback((e: React.DragEvent) => {
        if (!hasMediaData(e)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
        setIsDragOver(true);
    }, [hasMediaData]);

    /**
     * Handle drag leave - hide drop zone indicator.
     */
    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Only hide if we're leaving the wrapper entirely
        if (wrapperRef.current && !wrapperRef.current.contains(e.relatedTarget as Node)) {
            setIsDragOver(false);
        }
    }, []);

    /**
     * Handle drop - parse media data and trigger callback.
     */
    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        if (!hasMediaData(e)) return;

        try {
            const mediaJson = e.dataTransfer.getData(MEDIA_DRAG_TYPE);
            const media: UploadedMedia = JSON.parse(mediaJson);

            // Call the onImageDrop callback with media and drop position
            onImageDrop?.(media, { x: e.clientX, y: e.clientY });
        } catch (err) {
            console.error('Failed to parse dropped media:', err);
        }
    }, [hasMediaData, onImageDrop]);

    return (
        <div
            ref={wrapperRef}
            data-color-mode="dark"
            className={`md-editor-wrapper relative ${
                isDragOver ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-slate-800' : ''
            }`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {/* Drop zone overlay */}
            {isDragOver && (
                <div className="absolute inset-0 bg-purple-500/20 z-10 flex items-center justify-center pointer-events-none rounded-lg">
                    <div className="bg-purple-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>Drop to insert image</span>
                    </div>
                </div>
            )}

            <MDEditor
                value={value || ''}
                onChange={(val) => onChange?.(val || '')}
                height={height}
                preview="edit"
                textareaProps={{
                    placeholder: placeholder || 'Start writing your story...',
                }}
            />
            {/* Help text for hex linking and drag-drop */}
            <p className="mt-2 text-xs text-gray-400">
                Tip: Use <code className="bg-slate-700 px-1 rounded">[[hex-name]]</code> to link to other hexes.
                Drag images from the sidebar to insert them.
            </p>
        </div>
    );
}

export default Editor;

